"use client";
import { PairsSelector } from "@/features/common/pairs-module";
import { accountType } from "@/shared/api/browser/accountType";
import { paymentsClient, userClient } from "@/shared/rpc/clients";
import { chartBackgroundStore } from "@/shared/stores/ChartBackgroundStore";
import { pairsStore } from "@/shared/stores/PairsStore";
import { sessionCounterStore } from "@/shared/stores/SessionCounterStore";
import { tickerStore } from "@/shared/stores/TickerStore";
import { userStore } from "@/shared/stores/UserStore";
import TradingChart from "@/shared/ui/TradingChart/TradingChartWrapper";
import { trackEvent } from "@/utils/tag-manager";
import type { TradingPeriodMessage } from "@cronika/proto/common/trading_pb";
import { OnboardingStatus } from "@cronika/proto/common/user_pb";
import type { OnboardingStateResponse } from "@cronika/proto/user/user_pb";
import { observer } from "mobx-react-lite";
import { FC, lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { BetNotifications } from "./components/BetNotifications";
import { DemoProfitModal } from "./components/DemoProfitModal";
import { HistoryModal } from "./components/HistoryModal";
import { PairLockedBlock } from "./components/PairLockedBlock";
import { PairUnavailableModal } from "./components/PairUnavailableModal";
import { ReplayTutorialButton } from "./components/ReplayTutorialButton";
import { SessionCounterWindow } from "./components/SessionCounterWindow";
import { WelcomeModal } from "./components/WelcomeModal";
import { useDemoStats } from "./hooks/useDemoStats";
import { usePairAvailability } from "./hooks/usePairAvailability";
import { BetBlock } from "./modules/bet-module";
import { HistoryBlock } from "./modules/history-module";

const MobileBet = lazy(async () => {
  const mod = await import("./modules/mobile-bet-module");
  return { default: mod.MobileBet };
});

const MobileBetFallback = () => (
  <div className="relative h-full w-full flex items-center justify-center">
    <div className="flex flex-col items-center gap-3">
      <div className="w-10 h-10 border-4 border-[#f5a524] border-t-transparent rounded-full animate-spin" />
      <span className="text-sm text-default-500">Loading…</span>
    </div>
  </div>
);

export const Trading: FC = observer(() => {
  const [isDesktop, setIsDesktop] = useState<boolean | null>(null);
  const [isHorizontal, setIsHorizontal] = useState<boolean | null>(null);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [isOnboardingReplay, setIsOnboardingReplay] = useState(false);
  const [replayHasDeposited, setReplayHasDeposited] = useState(false);
  const [onboardingState, setOnboardingState] = useState<OnboardingStateResponse | null>(null);
  const [showPairUnavailableModal, setShowPairUnavailableModal] = useState(false);
  const [showDemoProfitModal, setShowDemoProfitModal] = useState(false);
  const { onDemoWin } = useDemoStats();
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<TradingPeriodMessage | undefined>(undefined);
  const [selectedTimePeriodSeconds, setSelectedTimePeriodSeconds] = useState<number | undefined>(undefined);
  const [hoveredButton, setHoveredButton] = useState<"call" | "put" | null>(null);
  const onboardingStateRequest = useRef<{ request: Promise<OnboardingStateResponse>; userId: string } | null>(null);
  const user = userStore.user;

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-height: 390px)");
    const update = () => setIsHorizontal(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!user) return;
    const userId = user.id;
    if (!userId) return;
    let cancelled = false;
    if (onboardingStateRequest.current?.userId !== userId) {
      onboardingStateRequest.current = { request: userClient.getOnboardingState({}), userId };
    }
    const request = onboardingStateRequest.current?.request;
    if (!request) return;
    request
      .then((state) => {
        if (cancelled) return;
        setOnboardingState(state);
        setShowWelcomeModal(
          state.status === OnboardingStatus.NOT_STARTED ||
            !user.countryCode ||
            (state.depositRecoveryPending && !state.depositRecoveryShown),
        );
      })
      .catch(() => {
        // Onboarding must never prevent access to the trading platform.
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    if (isDesktop !== null && user) {
      const currentAccountType = accountType.get();
      if (currentAccountType === "demo") {
        trackEvent("trading", "open_demo_trade_screen", "demo_trade_screen_opened");
      } else if (currentAccountType === "real") {
        trackEvent("trading", "open_real_trade_screen", "real_trade_screen_opened");
      }
    }
  }, [isDesktop, user]);

  useEffect(() => {
    if (isHorizontal !== null && user) {
      const currentAccountType = accountType.get();
      if (currentAccountType === "demo") {
        trackEvent("trading", "open_demo_trade_screen", "demo_trade_screen_opened");
      } else if (currentAccountType === "real") {
        trackEvent("trading", "open_real_trade_screen", "real_trade_screen_opened");
      }
    }
  }, [isHorizontal, user]);

  useEffect(() => {
    if (!user) return;

    const unsubscribe = accountType.subscribe((type) => {
      if (type === "demo") {
        trackEvent("trading", "open_demo_trade_screen", "demo_trade_screen_opened");
      } else if (type === "real") {
        trackEvent("trading", "open_real_trade_screen", "real_trade_screen_opened");
      }
    });

    return unsubscribe;
  }, [user]);

  const activePair = pairsStore.activePair;
  const currentPrice = activePair ? tickerStore.prices.get(activePair.symbol) : undefined;
  const { isPairLocked, availablePairs } = usePairAvailability();

  useEffect(() => {
    if (isPairLocked) {
      setShowPairUnavailableModal(true);
    }
  }, [isPairLocked]);

  useEffect(() => {
    if (!activePair) {
      document.title = "Cronika";
      return;
    }

    const priceStr =
      currentPrice !== undefined ? currentPrice.toFixed(currentPrice < 1 ? 6 : currentPrice < 100 ? 4 : 2) : "";

    document.title = priceStr ? `${priceStr} | ${activePair.symbol} | Cronika` : `${activePair.symbol} | Cronika`;
  }, [activePair, currentPrice]);

  const handleCloseWelcomeModal = useCallback(() => {
    setShowWelcomeModal(false);
    setIsOnboardingReplay(false);
    setReplayHasDeposited(false);
  }, []);

  const handleReplayOnboarding = useCallback(async () => {
    try {
      // hasDeposited is resolved before the modal opens (not inside it) so the
      // "skip bonus once deposited" check can never race a fast Skip click.
      const [next, depositAvailability] = await Promise.all([
        userClient.replayOnboarding({}),
        paymentsClient.getDepositAvailability({}).catch(() => undefined),
      ]);
      setOnboardingState(next);
      setReplayHasDeposited(depositAvailability?.hasDeposited ?? false);
      setIsOnboardingReplay(true);
      setShowWelcomeModal(true);
    } catch {
      // Onboarding must never prevent access to the trading platform.
    }
  }, []);

  useEffect(() => {
    const handler = () => void handleReplayOnboarding();
    window.addEventListener("cronika:onboarding-replay-requested", handler);
    return () => window.removeEventListener("cronika:onboarding-replay-requested", handler);
  }, [handleReplayOnboarding]);

  const handleClosePairUnavailableModal = () => setShowPairUnavailableModal(false);

  const handleDemoWin = useCallback(async () => {
    const shouldShow = await onDemoWin();
    if (shouldShow) setShowDemoProfitModal(true);
  }, [onDemoWin]);

  const handleCloseDemoProfitModal = useCallback(() => {
    setShowDemoProfitModal(false);
  }, []);

  if (isDesktop === null || isHorizontal === null) return null;

  return (
    <div className="relative h-full w-full overflow-hidden flex flex-col">
      {chartBackgroundStore.enabled &&
        chartBackgroundStore.preset !== "cronika" &&
        chartBackgroundStore.wideStyle &&
        chartBackgroundStore.imageUrl && (
          <div
            className="absolute inset-0 z-0 pointer-events-none bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${chartBackgroundStore.imageUrl})`,
              opacity: chartBackgroundStore.opacity,
            }}
          />
        )}
      <BetNotifications onDemoWin={handleDemoWin} />
      <WelcomeModal
        isOpen={showWelcomeModal}
        initialState={onboardingState}
        selectedTimePeriod={selectedTimePeriod}
        onClose={handleCloseWelcomeModal}
        isReplay={isOnboardingReplay}
        replayHasDeposited={replayHasDeposited}
      />
      <DemoProfitModal isOpen={showDemoProfitModal} onClose={handleCloseDemoProfitModal} />
      <HistoryModal />
      <PairUnavailableModal
        isOpen={showPairUnavailableModal}
        onClose={handleClosePairUnavailableModal}
        pairSymbol={activePair?.symbol}
      />
      {sessionCounterStore.enabled && <SessionCounterWindow />}
      {!isDesktop && !isHorizontal && (
        <div className="relative flex-1 min-h-0 overflow-hidden">
          <Suspense fallback={<MobileBetFallback />}>
            <MobileBet
              onTimePeriodChange={setSelectedTimePeriod}
              onTimePeriodSecondsChange={setSelectedTimePeriodSeconds}
              onButtonHover={setHoveredButton}
            >
              <div className="relative h-full w-full flex flex-col overflow-hidden">
                <PairsSelector />
                <div className="flex-1 min-h-0">
                  {!isPairLocked && (
                    <TradingChart
                      selectedTimePeriod={selectedTimePeriod}
                      selectedTimePeriodSeconds={selectedTimePeriodSeconds}
                      hoveredButton={hoveredButton}
                      mobileBottomOffset={228}
                    />
                  )}
                </div>
              </div>
            </MobileBet>
          </Suspense>
        </div>
      )}
      {isDesktop && (
        <div className="relative flex-col lg:flex-row w-full flex-1 min-h-0 flex overflow-hidden">
          <div className="flex-1 min-w-0 flex flex-col min-h-0">
            <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
              {!isPairLocked && (
                <TradingChart
                  selectedTimePeriod={selectedTimePeriod}
                  selectedTimePeriodSeconds={selectedTimePeriodSeconds}
                  hoveredButton={hoveredButton}
                />
              )}
            </div>
          </div>
          <div className="w-46.5 pr-1.5 py-4 flex flex-col flex-shrink-0 overflow-hidden">
            {isPairLocked ? (
              <PairLockedBlock availablePairs={availablePairs} activePairSymbol={activePair?.symbol} compact />
            ) : (
              <>
                <BetBlock
                  onTimePeriodChange={setSelectedTimePeriod}
                  onTimePeriodSecondsChange={setSelectedTimePeriodSeconds}
                  onButtonHover={setHoveredButton}
                />
                <HistoryBlock />
                <ReplayTutorialButton />
              </>
            )}
          </div>
        </div>
      )}
      {isHorizontal && !isDesktop && (
        <div className="relative flex flex-row w-full flex-1 min-h-0 overflow-hidden">
          <div className="flex-1 min-w-0 flex flex-col min-h-0">
            <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
              {!isPairLocked && (
                <TradingChart
                  selectedTimePeriod={selectedTimePeriod}
                  selectedTimePeriodSeconds={selectedTimePeriodSeconds}
                  hoveredButton={hoveredButton}
                />
              )}
            </div>
          </div>
          <div className="w-54 h-full mr-1.75 pt-4 ml-2.5 flex flex-col shrink-0">
            {isPairLocked ? (
              <PairLockedBlock availablePairs={availablePairs} activePairSymbol={activePair?.symbol} compact />
            ) : (
              <div className="overflow-auto">
                <BetBlock
                  onTimePeriodChange={setSelectedTimePeriod}
                  onTimePeriodSecondsChange={setSelectedTimePeriodSeconds}
                  onButtonHover={setHoveredButton}
                />
                <HistoryBlock />
                <ReplayTutorialButton />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});
